import React from 'react';
import { DiamondIcon, LayersIcon, SunIcon } from 'lucide-react';
import { ImagePlaceholder } from '../components/ui/ImagePlaceholder';
import { IconCard } from '../components/ui/IconCard';
import { BrandButton } from '../components/ui/BrandButton';
import { ScrollReveal } from '../components/ui/ScrollReveal';
export function AboutPage() {
  return <div className="flex flex-col w-full bg-[#FFF8F0]" data-id="element-202">
      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-10" data-id="element-203">
        <div className="container mx-auto px-4 md:px-8" data-id="element-204">
          <ScrollReveal data-id="element-205">
            <div className="relative w-full rounded-3xl overflow-hidden shadow-xl" data-id="element-206">
              <div className="absolute inset-0 bg-gradient-to-t from-[#E91E8C]/80 to-transparent z-10 mix-blend-multiply" data-id="element-207"></div>
              <ImagePlaceholder label="Founder lifestyle photo" aspectRatio="hero" className="rounded-3xl border-0" data-id="element-208" />
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16 z-20 text-center md:text-left" data-id="element-209">
                <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white drop-shadow-lg" data-id="element-210">
                  The Story Behind <br className="hidden md:block" data-id="element-211" /> Butter
                  Blonde
                </h1>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-20 md:py-28 bg-[#F8D0E0]" data-id="element-212">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl" data-id="element-213">
          {/* Full-width heading */}
          <ScrollReveal data-id="element-214">
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-[#2D1A1A] mb-12 md:mb-16 text-center" data-id="element-215">
              It started with a tangled mess of chains.
            </h2>
          </ScrollReveal>

          {/* Two columns: photo left, text right */}
          <div className="flex flex-col md:flex-row gap-12 md:gap-16 items-start mb-16 md:mb-20" data-id="element-216">
            <div className="w-full md:w-5/12" data-id="element-217">
              <ScrollReveal direction="right" data-id="element-218">
                <div className="relative p-3" data-id="element-219">
                  <div className="absolute inset-0 border-4 border-[#F8C8DC] rounded-3xl transform -rotate-2" data-id="element-220"></div>
                  <div className="absolute inset-0 border-4 border-[#F5D76E] rounded-3xl transform rotate-1 opacity-40" data-id="element-221"></div>
                  <ImagePlaceholder label="Founder photo" aspectRatio="portrait" className="relative z-10 shadow-xl" data-id="element-222" />
                </div>
              </ScrollReveal>
            </div>

            <div className="w-full md:w-7/12" data-id="element-223">
              <ScrollReveal direction="left" delay={200} data-id="element-224">
                <div className="space-y-6 font-body text-lg text-[#2D1A1A] leading-relaxed" data-id="element-225">
                  <p data-id="element-226">
                    I was always that girl trying to layer three different
                    necklaces, only to have them tangle, tarnish, or just not
                    sit right. I wanted the effortless "cool-girl" stack, but I
                    couldn't find pieces that were actually designed to work
                    together.
                  </p>
                  <p data-id="element-227">
                    That's how Butter Blonde was born. The name comes from that
                    feeling of something being "buttery smooth"—high quality,
                    seamless, and completely effortless.
                  </p>
                  <p data-id="element-228">
                    We design jewelry that isn't precious or intimidating. It's
                    meant to be lived in, mixed up, and stacked high. Whether
                    you're a minimalist who loves a single pearl or a maximalist
                    who wears five chains at once, these pieces are made for you
                    to tell your own story.
                  </p>
                </div>
              </ScrollReveal>
            </div>
          </div>

          {/* Full-width centered pull quote */}
          <ScrollReveal direction="fade" delay={300} data-id="element-229">
            <blockquote className="max-w-3xl mx-auto text-center" data-id="element-230">
              <div className="text-[#F5D76E] text-6xl opacity-60 mb-2 leading-none" data-id="element-231">
                "
              </div>
              <p className="font-script text-3xl md:text-4xl text-[#6B1D4A] leading-relaxed mb-6" data-id="element-232">
                I wanted to create jewelry that feels like you — effortless,
                layered, and totally yours.
              </p>
              <p className="font-body font-bold text-[#E91E8C] uppercase tracking-wider text-sm" data-id="element-233">
                — Jess, Creator of Butter Blonde
              </p>
            </blockquote>
          </ScrollReveal>
        </div>
      </section>

      {/* Brand Values */}
      <section className="py-20 bg-[#FFF8F0]" data-id="element-234">
        <div className="container mx-auto px-4 md:px-8" data-id="element-235">
          <ScrollReveal data-id="element-236">
            <div className="text-center mb-16" data-id="element-237">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#2D1A1A]" data-id="element-238">
                The Butter Standard
              </h2>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-239">
            <ScrollReveal direction="up" delay={100} className="h-full" data-id="element-240">
              <IconCard icon={<DiamondIcon className="w-8 h-8" data-id="element-242" />} title="Quality First" description="Crafted with premium materials that resist tarnishing, so your stack stays buttery smooth wear after wear." data-id="element-241" />
            </ScrollReveal>

            <ScrollReveal direction="up" delay={300} className="h-full" data-id="element-243">
              <IconCard icon={<LayersIcon className="w-8 h-8" data-id="element-245" />} title="Made to Layer" description="Every length, clasp, and chain weight is intentionally designed to sit perfectly together without tangling." data-id="element-244" />
            </ScrollReveal>

            <ScrollReveal direction="up" delay={500} className="h-full" data-id="element-246">
              <IconCard icon={<SunIcon className="w-8 h-8" data-id="element-248" />} title="For Every Day" description="Jewelry that moves with your life. From morning coffee to late-night drinks, it's effortless self-expression." data-id="element-247" />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA Section — subtle pinstripes */}
      <section className="py-24 md:py-32 text-center" style={{
      background: 'repeating-linear-gradient(90deg, #FFF3E8 0px, #FFF3E8 20px, #FFF8F0 20px, #FFF8F0 40px)'
    }} data-id="element-249">
        <div className="container mx-auto px-4" data-id="element-250">
          <ScrollReveal data-id="element-251">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#2D1A1A] mb-8" data-id="element-252">
              Ready to build your stack?
            </h2>
            <BrandButton href="/collections" variant="primary" className="text-lg px-12" data-id="element-253">
              Explore Collections
            </BrandButton>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}